/* The theme: which one this device shows, and keeping the account's choice and the device's
   in line. The look itself is CSS — one block of tokens per theme in src/themes/ — so all
   this does is set `data-theme` on <html> and remember the choice.

   The device's copy lives in localStorage, not IndexedDB with the other settings, because
   public/theme.js has to read it synchronously before the first paint. The account's copy is
   `profiles.theme`. */

export const THEMES = [
  { id: "dark", label: "Dark" },
  { id: "light", label: "Light" },
  { id: "carrot", label: "Carrot Corner" },
] as const;
export type ThemeId = (typeof THEMES)[number]["id"];

/** The same key and default as public/theme.js. */
export const THEME_KEY = "preflop.theme";
/** Set while this device holds a choice the profile has not been told of. */
const PENDING_KEY = "preflop.theme.pending";

/** Whatever was stored, by this build or another: a theme this build has, or the default. */
export const readTheme = (value: unknown): ThemeId =>
  THEMES.find((t) => t.id === value)?.id ?? "dark";

export type PushTheme = (theme: ThemeId) => Promise<void>;

/** What the logic needs of the browser, so that it can be tested without one. */
export interface ThemeHost {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
  /** Make the page show it. */
  show(theme: ThemeId): void;
}

export interface Themes {
  current(): ThemeId;
  /** The user picked one: shown and remembered at once, told to the profile if there is one. */
  choose(theme: ThemeId): Promise<void>;
  /** When a page opens signed in. The profile's theme is taken, unless this device holds a
      choice it never managed to push, which goes up instead. From then on a choice pushes. */
  reconcile(profileTheme: string, push: PushTheme): Promise<void>;
  /** On sign-out: the device keeps its look, but owes nothing to the next account and tells
      nobody until one is reconciled. */
  forget(): void;
  /** Called with every theme shown, for the pickers to follow each other. */
  onChange(listener: (theme: ThemeId) => void): void;
}

export function createThemes(host: ThemeHost): Themes {
  let pushTo: PushTheme | null = null;
  const listeners: ((theme: ThemeId) => void)[] = [];

  const current = (): ThemeId => readTheme(host.get(THEME_KEY));
  const show = (theme: ThemeId): void => {
    host.set(THEME_KEY, theme);
    host.show(theme);
    for (const l of listeners) l(theme);
  };
  async function push(theme: ThemeId): Promise<void> {
    if (!pushTo) return;
    try {
      await pushTo(theme);
      host.remove(PENDING_KEY);
    } catch {
      // Offline, most likely. The mark stays, and the next page to open pushes.
    }
  }

  return {
    current,
    async choose(theme) {
      show(theme);
      // Signed out there is nobody to tell, and nothing owed: signing in takes the account's.
      if (!pushTo) return;
      host.set(PENDING_KEY, "1");
      await push(theme);
    },
    async reconcile(profileTheme, to) {
      pushTo = to;
      if (host.get(PENDING_KEY)) return push(current());
      const theirs = readTheme(profileTheme);
      if (theirs !== current()) show(theirs);
    },
    forget() {
      pushTo = null;
      host.remove(PENDING_KEY);
    },
    onChange: (listener) => void listeners.push(listener),
  };
}

/* ---- the browser ---- */

const storage = (): Storage | null => {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null; // refused outright, as a private window may
  }
};

const browserHost: ThemeHost = {
  get: (key) => storage()?.getItem(key) ?? null,
  set: (key, value) => {
    try {
      storage()?.setItem(key, value);
    } catch {
      // Full, or refused: the theme still shows for this page.
    }
  },
  remove: (key) => storage()?.removeItem(key),
  show(theme) {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.theme = theme;
    document
      .querySelector('meta[name="color-scheme"]')
      ?.setAttribute("content", theme === "light" ? "light" : "dark");
  },
};

export const themes = createThemes(browserHost);
